<script lang="ts">
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, DisplayTemplate, GlobalCollectionId } from '#layers/data-model/public';

type ModelValue<AllowNone extends boolean> = AllowNone extends true ? (CompositePrimaryKey | null) : CompositePrimaryKey;

export interface InputSelectItemBlockProps<AllowNone extends boolean> extends InputBlockProps {
	collectionId: GlobalCollectionId;
	displayTemplate?: DisplayTemplate;
	modelValue?: ModelValue<AllowNone>;
	/**
	 * Allow the selection of `null`
	 *
	 * @default true
	 */
	allowNone?: AllowNone & boolean;
}

export interface InputSelectItemBlockEmits<AllowNone extends boolean> {
	'update:modelValue': [value: ModelValue<AllowNone>];
}

const __DsBlock = {
	hidden: true,
}</script>

<script setup lang="ts" generic="AllowNone extends boolean">
import { useOverlay } from '@nuxt/ui/composables';
import { computed, ref } from 'vue';
import { isNullishOrEmpty } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RenderCollectionDisplayTemplate from '#layers/blocks/components/Render/RenderCollectionDisplayTemplate.vue';
import { useItem } from '#layers/collection-query/public';
import { CollectionReads, useCollection } from '#layers/data-model/public';
import { LazyCollectionListingDrawer } from '#layers/listing/lazy';
import { useReadonly } from '#layers/ui/public';

const props = withDefaults(defineProps<InputSelectItemBlockProps<AllowNone>>(), {
	allowNone: true as any,
});
const emit = defineEmits<InputSelectItemBlockEmits<AllowNone>>();

const modelValue = computed<ModelValue<AllowNone> | undefined, ModelValue<AllowNone>>({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
});

;

const { collection } = useCollection(() => props.collectionId);

const { item, isLoading } = useItem({
	collection,
	itemKey: () => props.modelValue,
});

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);

const open = ref(false);

async function handleSelect() {
	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: props.collectionId,
			selection: modelValue.value !== null && modelValue.value !== undefined
				? [modelValue.value]
				: [],
			selectionMode: 'single',
		},
	});
	const result = await modal.open().result;

	if (result) {
		if (!isNullishOrEmpty(result.selection)) {
			modelValue.value = result.selection[0]!;
		}
		else if (props.allowNone) {
			modelValue.value = null as ModelValue<AllowNone>;
		}
	}
}

function handleOpen(open: boolean) {
	if (open) {
		handleSelect();
	}
}

function handleClear() {
	modelValue.value = null as ModelValue<AllowNone>;
}
</script>

<template>
	<DSelect
		variant="outline"
		color="neutral"
		class="h-10"
		:open
		:placeholder="$t('common.select_item')"
		:disabled
		:readonly
		:aria-readonly="readonly"
		@update:open="handleOpen"
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="preventKeyboardNavigation"
	>
		<DSkeleton v-if="modelValue && isLoading" class="size-full flex-1" />
		<RenderCollectionDisplayTemplate v-else-if="modelValue && item" :collection-id :display-template :item :read="CollectionReads.One" />

		<template v-if="!disabled && allowNone && !!modelValue && !readonly" #trailing>
			<DTooltip :text="$t('common.clear')">
				<DButton
					icon="lucide:x"
					color="neutral"
					variant="link"
					size="sm"
					@click.stop.prevent="handleClear()"
				/>
			</DTooltip>
		</template>
	</DSelect>
</template>
